// import { DropdownSelectOption } from '@paintscout/ui'
import type { Moment } from 'moment'
import moment from 'moment-timezone'
import { getEstimatorDayEvents } from '..'
import type { CalendarEvent } from 'paintscout'
import type { User } from '@paintscout/api'
import { getAvailabilityForDate } from './get-availability-gaps'

interface GetEstimatorDayAvailabilityParams {
  estimator: User
  date: Moment
  eventDuration: number
  events: CalendarEvent[]
  timezone: string
}

export function getEstimatorDayAvailability({
  estimator,
  date,
  eventDuration,
  events,
  timezone
}: GetEstimatorDayAvailabilityParams) {
  const dateStr = date.format('YYYY-MM-DD')
  const baseTimeSlots = [
    ...Array.from({ length: 68 }, (_, i) => {
      const hour = 5 + Math.floor(i / 4)
      const minute = (i % 4) * 15
      return moment.tz(dateStr, 'YYYY-MM-DD', timezone).set({ hour, minute, second: 0, millisecond: 0 })
    })
  ]
  const dayEvents = getEstimatorDayEvents({
    events,
    estimatorId: estimator.user_id,
    date,
    timezone
  })

  const availability = estimator.preferences?.availability || {}

  const dayAvailability: { start: string; end: string }[] = getAvailabilityForDate(
    availability,
    date.toDate(),
    timezone
  )

  // Helper to parse "08:00am" to a moment object on the given date
  function parseTime(timeStr: string, baseDate: Moment) {
    return moment.tz(baseDate.format('YYYY-MM-DD') + ' ' + timeStr, 'YYYY-MM-DD hh:mma', timezone)
  }

  // Filter out time slots that overlap with any event considering the eventDuration
  const availableTimeSlots = baseTimeSlots.filter((timeSlot) => {
    const timeMoment = moment.tz(dateStr, 'YYYY-MM-DD', timezone).set({
      hour: timeSlot.hour(),
      minute: timeSlot.minute(),
      second: 0,
      millisecond: 0
    })

    // Skip slots in the past
    if (timeMoment.isBefore(moment().tz(timezone))) {
      return false
    }

    const timeSlotEnd = timeMoment.clone().add(eventDuration, 'minutes')

    // Check if this time slot is within any available range for the day
    const inAvailableRange = dayAvailability.some((range) => {
      const rangeStart = parseTime(range.start, date)
      const rangeEnd = parseTime(range.end, date)
      return timeMoment.isSameOrAfter(rangeStart) && (timeSlotEnd.isSame(rangeEnd) || timeSlotEnd.isBefore(rangeEnd))
    })

    if (!inAvailableRange) {
      return false
    }

    return !dayEvents.some((event) => {
      const eventStart = moment(event.start).tz(timezone)
      const eventEnd = moment(event.end).tz(timezone)

      // If the event is marked as transparent (free), skip it
      if (event.transparency === 'transparent') {
        return false
      }

      // Check if the time slot overlaps with the event
      return (
        timeMoment.isBetween(eventStart, eventEnd, null, '[)') ||
        timeSlotEnd.isBetween(eventStart, eventEnd, null, '(]') ||
        (timeMoment.isSameOrBefore(eventStart) && timeSlotEnd.isSameOrAfter(eventEnd))
      )
    })
  })

  return availableTimeSlots
}
